
import Vue from 'vue';
import { Data as ArticleData, StreamElement } from '@root/modules/article/types/article';
import ArticleInfoCoverPaywall from '@root/views/article/components/info/ArticleInfoCoverPaywall.vue';
import ContentImage from '@core/components/UI/ContentImage.vue';
import ContentImageLightbox from '@core/components/UI/ContentImageLightbox.vue';
import CustomerStateHandler from '@customer/components/CustomerStateHandler.vue';
import PictureInfo from '@core/components/PictureInfo.vue';
import ContentFragment from '@core/components/ContentFragment.vue';
import articleConfig from '@root/modules/article/config/article.config';
import getArticleLeadPictureCropperProps from '@root/views/article/utils/getArticleLeadPictureCropperProps';
import getStreamElement from '@root/modules/article/utils/getStreamElement';

import dayjs from 'dayjs';
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter';
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore';

dayjs.extend(isSameOrAfter);
dayjs.extend(isSameOrBefore);

interface Props {
  article: ArticleData;
  size: string;
  position?: string;
}

interface Data {
  showLightbox: boolean;
}

interface Computed {
  leadElement: ArticleData['content']['leadElement']['content'][0] | null;
  picture: Record<string, any> | null;
  classes: Record<string, string | undefined>[];
  settings: Record<string, unknown> | null;
  streamElement: StreamElement | null;
  isLive: boolean;
  isEnded: boolean;
}

const sizeMap: Record<string, Record<string, unknown>> = articleConfig.infoCover;
const imageRatio = '16:9';

export default Vue.extend<Data, unknown, Computed, Props>({
  components: {
    ArticleInfoCoverPaywall,
    ContentImage,
    ContentImageLightbox,
    ContentFragment,
    CustomerStateHandler,
    PictureInfo,
  },
  props: {
    article: {
      type: Object,
      required: true,
    },
    size: {
      type: String,
      required: true,
    },
    position: {
      type: String,
      default: 'center',
      required: false,
    },
  },
  data() {
    return {
      showLightbox: false,
    };
  },
  computed: {
    leadElement() {
      const { leadElement } = this.article.content;
      const leadElementContent = leadElement.content.length > 0 ? leadElement.content[0] : null;
      const isNotPicture = leadElementContent?.attrs?.type?.toLowerCase() !== 'image';
      if (isNotPicture && leadElementContent && leadElementContent.attrs) {
        leadElementContent.attrs.lead = true;
      }

      return isNotPicture ? leadElementContent : null;
    },
    picture() {
      const metaImage = this.article.metaImage;

      if (!metaImage) {
        return null;
      }

      if (!metaImage.attributes) {
        return null;
      }

      const baseCropperData = getArticleLeadPictureCropperProps(metaImage?.attributes?.cropperData, imageRatio);

      metaImage.attributes.cropperData = {
        ...sizeMap[this.size],
        ...metaImage?.attributes?.cropperData,
        base: baseCropperData,
      };

      return metaImage;
    },
    classes() {
      return [
        {
          [`C-article-info-cover--${this.position}`]: this.position,
          [`C-article-info-cover--${this.size}`]: this.size,
        },
      ];
    },
    settings() {
      let settings = null;

      if (this.leadElement) {
        const { embed } = this.$channelConfig('page').article.component.info;
        settings = embed || settings;
      }
      return settings;
    },
    streamElement() {
      return getStreamElement(this.article);
    },
    isLive() {
      if (this.streamElement) {
        return dayjs().isSameOrAfter(this.streamElement.start) && dayjs().isSameOrBefore(this.streamElement.end);
      }

      return false;
    },
    isEnded() {
      if (this.streamElement) {
        return dayjs().isSameOrAfter(this.streamElement.end);
      }

      return false;
    },
  },
});
